import { classNames, React } from 'libs/react';
import { Badge } from 'components/Badge';
import { Status } from 'components/Status';
import { Text } from 'components/Text';
import { highlightSubstring } from 'components/documentation/highlightText';
import { useDocumentationPanel } from 'components/documentation/DocumentationPanelContext';
import { useRoute } from 'components/router/Routing';
import { useUser } from 'components/user/UserProvider';
import { SEARCH_ENTITIES, searchEntityRoute, searchEntityStatus } from '../searchEntities';
import './SearchResultItem.scss';
/**
 * One result, whatever its type.
 *
 * Rendered straight from the columns of the index rather than through the
 * per-entity tables: those are typed on the full GraphQL entity, so feeding
 * them would mean reloading every record and reintroducing exactly the queries
 * on translations, emails and phones that the index exists to avoid.
 *
 * @component SearchResultItem
 * @since 1.0.0
 */
export function SearchResultItem(props) {
    const { item, highlighted, ident, term } = props;
    const route = useRoute();
    const { can } = useUser();
    const { openPanel } = useDocumentationPanel();
    const descriptor = SEARCH_ENTITIES[item.entity];
    // A bucket shipped by the backend before the frontend knows about it must
    // not break the screen.
    if (!descriptor) {
        return null;
    }
    const target = searchEntityRoute(item);
    const status = searchEntityStatus(item.entity, item.status);
    const secondary = descriptor.secondary && can(descriptor.secondary.permission)
        ? descriptor.secondary
        : null;
    const secondaryTarget = secondary ? secondary.route(item) : null;
    /**
     * @function onPress
     * @since 1.0.0
     * @hidden
     */
    function onPress() {
        if (props.onSelect) {
            props.onSelect(item);
        }
        /*
         * An article has no route: documentation.view lives in PublicLayout, so
         * inside the app the drawer is where it opens. Handled here rather than in
         * each caller, so the preview and the results screen behave the same.
         */
        if (item.entity === 'DOCUMENTATION') {
            openPanel({ articleId: item.uuid });
            return;
        }
        if (target) {
            route.push(target);
        }
    }
    /**
     * Marks the term inside a value. highlightSubstring escapes the text before
     * wrapping the matches, so the result is safe to render as HTML.
     *
     * @function marked
     * @since 1.0.0
     * @hidden
     */
    function marked(value) {
        return term ? highlightSubstring(value, term) : value;
    }
    /**
     * The secondary link sits inside the row, so its click has to be stopped
     * from also triggering the row. Text.onClick takes no event, hence the
     * wrapping element.
     *
     * @function onSecondaryPress
     * @since 1.0.0
     * @hidden
     */
    function onSecondaryPress(e) {
        e.stopPropagation();
        if (props.onSelect) {
            props.onSelect(item);
        }
        if (secondaryTarget) {
            route.push(secondaryTarget);
        }
    }
    const className = classNames('search-result-item', { 'search-result-item--is-highlighted': highlighted === true }, {
        'search-result-item--is-disabled': target == null && item.entity !== 'DOCUMENTATION'
    }, props.className);
    return (React.createElement("div", { className: className, id: ident, role: "option", "aria-selected": highlighted === true, onClick: onPress },
        React.createElement("div", { className: "search-result-item-main" },
            React.createElement(Text, { className: "search-result-item-title", value: marked(item.title), html: term != null && term !== '', ellipsize: true }),
            item.subtitle && (React.createElement(Text, { className: "search-result-item-infos", value: marked(item.subtitle), html: term != null && term !== '', ellipsize: true }))),
        React.createElement("div", { className: "search-result-item-meta" },
            item.code && (React.createElement(Badge, { className: "search-result-item-code", label: String(item.code) })),
            status && (React.createElement(Status, { className: "search-result-item-status", style: "plain", label: status.label, color: status.color })),
            secondaryTarget && (React.createElement("div", { className: "search-result-item-secondary", onClick: onSecondaryPress },
                React.createElement(Text, { value: secondary.label() }))))));
}
